import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useState } from 'react';

import DepositPanel from '../deposit/DepositPanel';
import { useSelection } from '../selection/SelectionContext';
import CustomerSwapPanel from '../swap/CustomerSwapPanel';
import { brand } from '../theme';
import WithdrawPanel from '../withdraw/WithdrawPanel';

type WalletTab = 'deposit' | 'withdraw' | 'swap';

export interface WalletActionsModalProps {
  open: boolean;
  onClose: () => void;
  /** Which tab to open with. */
  initialTab?: WalletTab;
}

/**
 * Modal with Deposit / Withdraw tabs and Currency → Network pickers.
 * Deposit address / withdraw form appear only after both dropdowns resolve to a mint.
 */
export default function WalletActionsModal({
  open,
  onClose,
  initialTab = 'deposit',
}: WalletActionsModalProps) {
  const {
    selection,
    networks,
    loadingNetworks,
    provisioning,
    selectMint,
    clearMint,
  } = useSelection();

  const [tab, setTab] = useState<WalletTab>(initialTab);
  const [currency, setCurrency] = useState('');
  const [chain, setChain] = useState('');

  useEffect(() => {
    if (open) {
      setTab(initialTab);
    }
  }, [open, initialTab]);

  const currencies = useMemo(() => {
    const symbols = new Set<string>();
    for (const group of networks) {
      for (const token of group.tokens) {
        symbols.add(token.symbol);
      }
    }
    return [...symbols].sort((a, b) => a.localeCompare(b));
  }, [networks]);

  const chainsForCurrency = useMemo(() => {
    if (!currency) return [];
    const chains: string[] = [];
    for (const group of networks) {
      if (group.tokens.some((t) => t.symbol === currency)) {
        chains.push(group.chain);
      }
    }
    return chains.sort((a, b) => a.localeCompare(b));
  }, [networks, currency]);

  // Hydrate draft dropdowns from a restored/active selection.
  useEffect(() => {
    if (selection) {
      setCurrency(selection.symbol);
      setChain(selection.chain);
    }
  }, [selection?.mintId, selection?.symbol, selection?.chain]);

  // Auto-select network when only one chain offers the chosen currency.
  useEffect(() => {
    if (!currency) return;
    if (chainsForCurrency.length === 1 && chain !== chainsForCurrency[0]) {
      const only = chainsForCurrency[0];
      setChain(only);
      selectMint(currency, only);
    }
  }, [currency, chainsForCurrency, chain, selectMint]);

  const handleCurrencyChange = (next: string) => {
    setCurrency(next);
    setChain('');
    clearMint();
  };

  const handleChainChange = (next: string) => {
    setChain(next);
    if (currency && next) {
      selectMint(currency, next);
    }
  };

  const ready = selection != null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: `${brand.cardRadius}px`,
          border: `1px solid ${brand.hairline}`,
          backgroundColor: brand.surface,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1,
          pt: 1,
          borderBottom: `1px solid ${brand.hairline}`,
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, value: WalletTab) => setTab(value)}
          variant="fullWidth"
          sx={{
            flex: 1,
            minHeight: 48,
            '& .MuiTab-root': {
              minHeight: 48,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: 15,
            },
          }}
        >
          <Tab label="Deposit" value="deposit" />
          <Tab label="Withdraw" value="withdraw" />
          <Tab label="Swap" value="swap" />
        </Tabs>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={{ mr: 0.5, color: brand.faint }}
        >
          ✕
        </IconButton>
      </Box>

      <DialogContent sx={{ px: { xs: 2.25, sm: 3 }, py: 2.5 }}>
        <Stack spacing={2} data-testid="wallet-actions-modal">
          {tab === 'swap' ? (
            <CustomerSwapPanel embedded />
          ) : (
            <>
              <FormControl fullWidth size="small" disabled={loadingNetworks || provisioning}>
                <InputLabel id="wallet-currency-label">Currency</InputLabel>
                <Select
                  labelId="wallet-currency-label"
                  label="Currency"
                  value={currency}
                  onChange={(e) => handleCurrencyChange(String(e.target.value))}
                  inputProps={{ 'aria-label': 'Currency' }}
                  MenuProps={{ disablePortal: true }}
                >
                  {currencies.map((symbol) => (
                    <MenuItem key={symbol} value={symbol}>
                      {symbol}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl
                fullWidth
                size="small"
                disabled={!currency || loadingNetworks || provisioning}
              >
                <InputLabel id="wallet-network-label">Network</InputLabel>
                <Select
                  labelId="wallet-network-label"
                  label="Network"
                  value={chain}
                  onChange={(e) => handleChainChange(String(e.target.value))}
                  inputProps={{ 'aria-label': 'Network' }}
                  MenuProps={{ disablePortal: true }}
                >
                  {chainsForCurrency.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {!currency || !chain ? (
                <Alert severity="info">
                  Select a currency and network to{' '}
                  {tab === 'deposit' ? 'deposit' : 'withdraw'}.
                </Alert>
              ) : provisioning && !ready ? (
                <Box
                  sx={{
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: brand.goldDeep,
                    animation: 'chd-pulse 2.2s ease-in-out infinite',
                  }}
                >
                  Provisioning your {chain} wallet…
                </Box>
              ) : null}

              {ready && tab === 'deposit' ? <DepositPanel embedded /> : null}
              {ready && tab === 'withdraw' ? <WithdrawPanel embedded /> : null}

              {currency && chain && !ready && !provisioning ? (
                <Typography sx={{ fontSize: 13, color: brand.faint }}>
                  Waiting for wallet…
                </Typography>
              ) : null}
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
